import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ImageCarousel = ({ images, alt, objectPosition, autoPlay = true, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  // Auto-rotate effect
  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;
    
    const timer = setInterval(() => {
      goToNext();
    }, interval);
    
    return () => clearInterval(timer);
  }, [autoPlay, interval, goToNext, images.length]);

  // Get object position for current image - can be an array with specific positions per image
  const getObjectPosition = () => {
    if (Array.isArray(objectPosition)) {
      return objectPosition[currentIndex] || 'center';
    }
    return objectPosition || 'center';
  };

  return (
    <div className="relative w-full h-full group">
      {/* Image */}
      <img
        src={images[currentIndex]}
        alt={`${alt} ${currentIndex + 1}`}
        className="w-full h-full object-cover"
        style={{ objectPosition: getObjectPosition() }}
      />

      {/* Navigation Buttons - always visible on mobile, hover on desktop */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-[#3a2f28]/80 hover:bg-[#3a2f28] text-[#f5f1e8] p-2 md:p-3 rounded-full transition-all duration-300 opacity-70 md:opacity-0 md:group-hover:opacity-100 backdrop-blur-sm z-30"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-[#3a2f28]/80 hover:bg-[#3a2f28] text-[#f5f1e8] p-2 md:p-3 rounded-full transition-all duration-300 opacity-70 md:opacity-0 md:group-hover:opacity-100 backdrop-blur-sm z-30"
            aria-label="Next image"
          >
            <ChevronRight size={20} className="md:w-6 md:h-6" />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-30">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-[#f5f1e8] w-6 md:w-8'
                    : 'bg-[#f5f1e8]/50 hover:bg-[#f5f1e8]/80'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageCarousel;